import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { softDelete } from '../../../lib/softDelete';
import { supabase } from '../../../lib/supabase';
import type { TaskFilters, TaskInput, TaskRow, TaskStatus } from './tasks.schema';

export const tasksQueryKey = (orgId: string | null, filters?: TaskFilters) =>
  ['tasks', orgId, filters] as const;

function emptyToNull(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

interface TasksSelectRow {
  id: string;
  organization_id: string;
  project_id: string | null;
  title: string;
  description: string | null;
  status: string;
  priority: string | null;
  assignee: string | null;
  due_date: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

function toTaskRow(row: TasksSelectRow): TaskRow {
  return {
    ...row,
    status: row.status as TaskRow['status'],
    priority: row.priority as TaskRow['priority'] | null,
  };
}

async function fetchTasks(orgId: string, filters: TaskFilters): Promise<TaskRow[]> {
  let query = supabase
    .from('tasks' as never)
    .select(
      'id, organization_id, project_id, title, description, status, priority, assignee, due_date, completed_at, created_at, updated_at',
    )
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });

  if (filters.projectId === 'none') {
    query = query.is('project_id', null);
  } else if (filters.projectId && filters.projectId !== 'all') {
    query = query.eq('project_id', filters.projectId);
  }

  if (!filters.showCompleted) {
    query = query.not('status', 'in', '("done","cancelled")');
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data as TasksSelectRow[]).map(toTaskRow);
}

export function useTasks(orgId: string | null, filters: TaskFilters) {
  return useQuery({
    queryKey: tasksQueryKey(orgId, filters),
    queryFn: () => {
      if (!orgId) return Promise.resolve([]);
      return fetchTasks(orgId, filters);
    },
    enabled: Boolean(orgId),
    staleTime: 30 * 1000,
  });
}

interface TaskInsertPayload {
  organization_id: string;
  project_id: string | null;
  title: string;
  description: string | null;
  status: string;
  priority: string | null;
  due_date: string | null;
  created_by: string;
}

interface TaskUpdatePayload {
  project_id: string | null;
  title: string;
  description: string | null;
  status: string;
  priority: string | null;
  due_date: string | null;
}

function toPayload(values: TaskInput): TaskUpdatePayload {
  return {
    project_id: emptyToNull(values.project_id),
    title: values.title.trim(),
    description: emptyToNull(values.description),
    status: values.status,
    priority: values.priority ?? null,
    due_date: emptyToNull(values.due_date),
  };
}

export function useUpsertTask(orgId: string | null, userId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, values }: { id: string | null; values: TaskInput }) => {
      if (!orgId || !userId) throw new Error('Missing task context');

      if (id) {
        const payload: TaskUpdatePayload = toPayload(values);
        const { error } = await supabase
          .from('tasks' as never)
          .update(payload as never)
          .eq('id', id)
          .eq('organization_id', orgId);
        if (error) throw error;
        return;
      }

      const payload: TaskInsertPayload = {
        ...toPayload(values),
        organization_id: orgId,
        created_by: userId,
      };
      const { error } = await supabase.from('tasks' as never).insert(payload as never);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks', orgId] });
      // The customer page lists open tasks through its own key.
      await queryClient.invalidateQueries({ queryKey: ['customer-profile'] });
    },
  });
}

export function useQuickAddTask(orgId: string | null, userId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ title, project_id }: { title: string; project_id: string | null }) => {
      if (!orgId || !userId) throw new Error('Missing task context');
      const payload: TaskInsertPayload = {
        organization_id: orgId,
        project_id,
        title: title.trim(),
        description: null,
        status: 'todo',
        priority: null,
        due_date: null,
        created_by: userId,
      };
      const { error } = await supabase.from('tasks' as never).insert(payload as never);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks', orgId] });
      // The customer page lists open tasks through its own key.
      await queryClient.invalidateQueries({ queryKey: ['customer-profile'] });
    },
  });
}

export function useUpdateTaskStatus(orgId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: TaskStatus }) => {
      if (!orgId) throw new Error('Missing org context');
      const { error } = await supabase
        .from('tasks' as never)
        .update({ status } as never)
        .eq('id', id)
        .eq('organization_id', orgId);
      if (error) throw error;
    },
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: ['tasks', orgId] });
      const snapshots = queryClient.getQueriesData<TaskRow[]>({ queryKey: ['tasks', orgId] });
      snapshots.forEach(([key, data]) => {
        if (!data) return;
        queryClient.setQueryData<TaskRow[]>(
          key,
          data.map((task) => (task.id === id ? { ...task, status } : task)),
        );
      });
      return { snapshots };
    },
    onError: (_error, _variables, context) => {
      context?.snapshots.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks', orgId] });
      // The customer page lists open tasks through its own key.
      await queryClient.invalidateQueries({ queryKey: ['customer-profile'] });
    },
  });
}

export function useSoftDeleteTask(orgId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!orgId) throw new Error('Missing org context');
      await softDelete('tasks', id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks', orgId] });
      // The customer page lists open tasks through its own key.
      await queryClient.invalidateQueries({ queryKey: ['customer-profile'] });
    },
  });
}
